/**
 * Cookie utilities for cross-port auth sharing.
 *
 * - localhost: no domain attribute, so the cookie is shared across all ports.
 * - production: dot-prefixed **current hostname**, e.g. `.app.commanderai.com`.
 *
 * ## Do not broaden this to the apex domain
 *
 * These cookies hold an auth token and are written by JS, so they are readable
 * via `document.cookie` by anything on a receiving origin — not HttpOnly.
 *
 * Because the app is served from `app.commanderai.com`, the derived domain is
 * `.app.commanderai.com`, which reaches that host and its subdomains only.
 *
 * The change to avoid is widening this to `.commanderai.com` in order to share
 * a session across subdomains — `app.`, `haulercentral.`, `sign-`, and so on.
 * That is a reasonable thing to want in a subdomain architecture, and it is
 * exactly what would send a readable auth token to **every** `commanderai.com`
 * host, including `widget.commanderai.com`.
 *
 * (The apex itself is a separate Framer landing site, so this app is never
 * served from there — but that only rules out one route to the same outcome.)
 *
 * That origin serves the website widget inside an iframe on arbitrary hauler
 * websites, so it is exposed on third-party pages by design. Handing it a live
 * session is a far worse trade than the sharing convenience is worth. See
 * plans/widget-builder.md, Phase 0.
 */

export const setCookie = (name: string, value: string, days: number = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const hostname = window.location.hostname;
  const encodedValue = encodeURIComponent(value);

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // For localhost: DON'T set domain - browser will share across all localhost:* ports
    document.cookie = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  } else {
    // Dot-prefixed *current* hostname, not the apex — see the note above.
    const domain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    document.cookie = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/;domain=.${domain};SameSite=Lax`;
  }
};

export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const raw = c.substring(nameEQ.length, c.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        // Value was not encoded (pre-existing cookie), return as-is
        return raw;
      }
    }
  }

  return null;
};

export const deleteCookie = (name: string) => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // For localhost: NO domain attribute to match how cookie was set
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
  } else {
    // Must mirror how setCookie scoped it, or the delete silently misses.
    const domain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.${domain}`;
  }
};

/**
 * Clear all auth-related storage (localStorage and cookies)
 * Returns a Promise to ensure deletion is complete before proceeding
 */
export const clearAuthStorage = async (keys: {
  TOKEN: string;
  USER: string;
  USER_DETAILS: string;
  SELECTED_COMPANY: string;
}): Promise<void> => {
  // Clear localStorage
  localStorage.removeItem(keys.TOKEN);
  localStorage.removeItem(keys.USER);
  localStorage.removeItem(keys.USER_DETAILS);
  localStorage.removeItem(keys.SELECTED_COMPANY);

  // Clear cookies
  deleteCookie(keys.TOKEN);
  deleteCookie(keys.USER);

  // Force a small delay to ensure browser completes the operations
  // This is especially important for cookies which may be asynchronous internally
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
};
